<script>
  import { createEventDispatcher } from 'svelte';
  import LoadingSpinner from '../common/LoadingSpinner.svelte';
  import EmptyState from '../common/EmptyState.svelte';
  
  const dispatch = createEventDispatcher();
  
  // Props
  export let data = [];
  export let columns = [];
  export let loading = false;
  export let empty = false;
  export let sortable = true;
  export let selectable = false;
  export let striped = false;
  export let bordered = false;
  export let hover = true;
  export let compact = false;
  export let responsive = true;
  export let size = 'md'; // 'sm', 'md', 'lg'
  export let emptyMessage = '표시할 데이터가 없습니다';
  export let loadingMessage = '데이터를 불러오는 중...';
  export let stickyHeader = false;

  // Internal state
  let sortField = null;
  let sortDirection = 'asc';
  let selectedRows = new Set();
  let allSelected = false;

  // Computed
  $: sortedData = sortable && sortField 
    ? [...data].sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        
        // 숫자 비교
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        }
        
        // 문자열 비교
        const aStr = String(aVal || '').toLowerCase();
        const bStr = String(bVal || '').toLowerCase();
        const compareResult = aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
        return sortDirection === 'asc' ? compareResult : -compareResult;
      })
    : data;

  $: tableClass = [
    'table',
    striped && 'table-striped',
    bordered && 'table-bordered',
    hover && 'table-hover',
    compact && 'table-compact',
    size !== 'md' && `table-${size}`,
    selectable && 'table-selectable',
    stickyHeader && 'table-sticky-header'
  ].filter(Boolean).join(' ');

  $: containerClass = [
    'table-container',
    responsive && 'table-responsive'
  ].filter(Boolean).join(' ');

  $: allSelected = selectedRows.size === data.length && data.length > 0;

  // Methods
  function handleSort(field) {
    if (!sortable) return;
    
    const column = columns.find(col => col.field === field);
    if (column && column.sortable === false) return;
    
    if (sortField === field) {
      sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      sortField = field;
      sortDirection = 'asc';
    }
    
    dispatch('sort', { field, direction: sortDirection, data: sortedData });
  }

  function handleRowSelect(row, selected) {
    if (selected) {
      selectedRows.add(row);
    } else {
      selectedRows.delete(row);
    }
    selectedRows = new Set(selectedRows);
    dispatch('select', { row, selected, selectedRows: Array.from(selectedRows) });
  }

  function handleSelectAll(selected) {
    if (selected) {
      selectedRows = new Set(data);
    } else {
      selectedRows = new Set();
    }
    dispatch('selectAll', { selected, selectedRows: Array.from(selectedRows) });
  }

  function handleRowClick(row, index, event) {
    // 체크박스나 버튼 클릭은 제외
    if (event.target.type === 'checkbox' || event.target.tagName === 'BUTTON' || event.target.closest('button')) {
      return;
    }
    dispatch('rowClick', { row, index, event });
  }

  function handleCellClick(row, column, index, event) {
    // 체크박스나 버튼 클릭은 제외
    if (event.target.type === 'checkbox' || event.target.tagName === 'BUTTON' || event.target.closest('button')) {
      return;
    }
    dispatch('cellClick', { row, column, index, event });
  }

  function getSortIcon(column) {
    if (!sortable || column.sortable === false) return '';
    if (sortField !== column.field) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  }

  function getCellContent(row, column, index) {
    if (column.component) {
      return { type: 'component', component: column.component };
    }
    if (column.render) {
      return { type: 'html', content: column.render(row[column.field], row, index) };
    }
    return { type: 'text', content: row[column.field] || '' };
  }
</script>

<div class={containerClass}>
  <table class={tableClass}>
    <thead>
      <tr>
        {#if selectable}
          <th class="table-select-header">
            <input 
              type="checkbox" 
              checked={allSelected}
              indeterminate={selectedRows.size > 0 && selectedRows.size < data.length}
              on:change={e => handleSelectAll(e.target.checked)}
              aria-label="모두 선택"
            />
          </th>
        {/if}
        
        {#each columns as column}
          <th 
            class:sortable={sortable && column.sortable !== false}
            class:sorted={sortField === column.field}
            class:sort-asc={sortField === column.field && sortDirection === 'asc'}
            class:sort-desc={sortField === column.field && sortDirection === 'desc'}
            class={column.headerClass || ''}
            style={column.width ? `width: ${column.width}` : ''}
            on:click={() => handleSort(column.field)}
            role={sortable && column.sortable !== false ? 'button' : null}
            tabindex={sortable && column.sortable !== false ? 0 : null}
            on:keydown={e => {
              if ((e.key === 'Enter' || e.key === ' ') && sortable && column.sortable !== false) {
                e.preventDefault();
                handleSort(column.field);
              }
            }}
          >
            <span class="table-header-content">
              {column.title}
              {#if sortable && column.sortable !== false}
                <span class="sort-indicator" aria-hidden="true">
                  {getSortIcon(column)}
                </span>
              {/if}
            </span>
          </th>
        {/each}
      </tr>
    </thead>
    
    <tbody>
      {#if loading}
        <tr>
          <td colspan={columns.length + (selectable ? 1 : 0)} class="table-loading">
            <div class="loading-content">
              <LoadingSpinner size="sm" />
              <span>{loadingMessage}</span>
            </div>
          </td>
        </tr>
      {:else if empty || sortedData.length === 0}
        <tr>
          <td colspan={columns.length + (selectable ? 1 : 0)} class="table-empty">
            <slot name="empty">
              <EmptyState 
                message={emptyMessage}
                icon="📄"
              />
            </slot>
          </td>
        </tr>
      {:else}
        {#each sortedData as row, index}
          <tr 
            class:selected={selectedRows.has(row)}
            class={row.className || ''}
            data-row-id={row.id || index}
            on:click={e => handleRowClick(row, index, e)}
            role={$$slots.default ? 'button' : null}
            tabindex={$$slots.default ? 0 : null}
          >
            {#if selectable}
              <td class="table-select-cell">
                <input 
                  type="checkbox" 
                  checked={selectedRows.has(row)}
                  on:change={e => handleRowSelect(row, e.target.checked)}
                  aria-label={`행 ${index + 1} 선택`}
                />
              </td>
            {/if}
            
            {#each columns as column}
              <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-tabindex -->
              <td 
                class="table-cell table-cell-{column.field} {column.cellClass || ''} {column.clickable ? 'clickable-cell' : ''}"
                data-label={column.title}
                style={column.cellStyle || ''}
                on:click={e => column.clickable ? handleCellClick(row, column, index, e) : null}
                tabindex={column.clickable ? -1 : null}
              >
                {#if column.component}
                  <svelte:component 
                    this={column.component} 
                    value={row[column.field]} 
                    {row} 
                    {column}
                    {index}
                  />
                {:else if column.render}
                  {@html column.render(row[column.field], row, index)}
                {:else}
                  {row[column.field] || ''}
                {/if}
              </td>
            {/each}
          </tr>
        {/each}
      {/if}
    </tbody>
    
    {#if $$slots.footer}
      <tfoot>
        <slot name="footer" />
      </tfoot>
    {/if}
  </table>
</div>

<style>
  .table-container {
    width: 100%;
    overflow-x: auto;
    margin-bottom: var(--spacing-lg);
  }

  .table-responsive {
    border-radius: var(--border-radius-md);
    box-shadow: var(--shadow-sm);
  }

  .table-header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .table th.sortable {
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s ease;
  }

  .table th.sortable:hover {
    background-color: var(--color-gray-100);
  }

  .table th.sortable:focus {
    outline: 2px solid var(--primary-color);
    outline-offset: -2px;
  }

  .sort-indicator {
    margin-left: var(--spacing-xs);
    opacity: 0.5;
    font-size: var(--font-size-sm);
    min-width: 12px;
    display: inline-block;
    text-align: center;
  }

  .table th.sorted .sort-indicator {
    opacity: 1;
    color: var(--primary-color);
  }

  .table-select-header,
  .table-select-cell {
    width: 50px;
    text-align: center;
  }

  .table-select-header input,
  .table-select-cell input {
    margin: 0;
    transform: scale(1.1);
  }

  .table-loading,
  .table-empty {
    text-align: center;
    padding: var(--spacing-xl);
  }

  .loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-md);
    color: var(--text-muted);
  }

  .table tbody tr.selected {
    background-color: rgba(50, 115, 220, 0.1);
    border-left: 3px solid var(--primary-color);
  }

  .clickable-cell {
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .clickable-cell:hover {
    background-color: var(--color-gray-100);
  }

  .clickable-cell:focus {
    outline: 2px solid var(--primary-color);
    outline-offset: -2px;
  }

  .table-sticky-header thead th {
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: var(--color-gray-50);
    box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.1);
  }

  /* 모바일 반응형 */
  @media (max-width: 768px) {
    .table-responsive {
      border-radius: 0;
      box-shadow: none;
    }

    .table-cell::before {
      content: attr(data-label) ': ';
      font-weight: var(--font-weight-semibold);
      color: var(--text-muted);
      display: inline-block;
      min-width: 80px;
    }
  }

  /* 모바일 스택 모드 */
  @media (max-width: 576px) {
    .table-container.table-responsive .table,
    .table-container.table-responsive .table thead,
    .table-container.table-responsive .table tbody,
    .table-container.table-responsive .table th,
    .table-container.table-responsive .table td,
    .table-container.table-responsive .table tr {
      display: block;
    }

    .table-container.table-responsive .table thead tr {
      position: absolute;
      top: -9999px;
      left: -9999px;
    }

    .table-container.table-responsive .table tr {
      background: var(--color-white);
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius-md);
      margin-bottom: var(--spacing-md);
      padding: var(--spacing-md);
    }

    .table-container.table-responsive .table td {
      border: none;
      padding: var(--spacing-sm) 0;
      position: relative;
      padding-left: 50%;
    }

    .table-container.table-responsive .table td::before {
      content: attr(data-label);
      position: absolute;
      left: 0;
      width: 45%;
      padding-right: var(--spacing-sm);
      white-space: nowrap;
      font-weight: var(--font-weight-semibold);
      color: var(--text-muted);
      font-size: var(--font-size-sm);
    }
  }
</style> 